<template>
  <modal-dialog
    size="lg"
    ref="modalTarget"
    heading="Defer reservation"
    heading-icon="flip-forward"
    show-header-separator
    :has-opener="false"
    :color="isReservationDeferred ? 'neutral' : 'error'"
    :cancel-action-copy="isReservationDeferred ? 'Close' : 'Cancel'"
    @modal-has-closed="resetModal"
  >
    <template #modal-body>
      <section class="my-6">
        <deferral-success
          v-if="isReservationDeferred"
          :statement="tempStatement"
        />

        <template v-else>
          <new-statement-fields
            v-if="shouldCreateNewStatement"
            ref="newStatementFieldsRef"
            @click:back="shouldCreateNewStatement = false"
            @create-statement="deferWithNewStatement"
          />

          <eligible-statements-fields
            v-else
            ref="eligibleStatementsFieldsRef"
            :isLoading="isPending"
            :statements="eligibleStatements?.data || []"
            @click:new-statement="shouldCreateNewStatement = true"
          />
        </template>
      </section>
    </template>

    <template #primary-action>
      <button
        v-if="!isReservationDeferred"
        class="btn btn-error"
        @click="handleDefer"
        :disabled="isLoading"
      >
        {{ isLoading ? 'Deferring...' : 'Defer reservation' }}
      </button>
    </template>
  </modal-dialog>
</template>

<script setup>
import { ref, computed, inject } from 'vue';
import { useReservationDrawerStore } from '@/stores/reservations';
import { useCreateStatement } from '@/services/statements/queries';
import { useGetEligibleStatements, useDeferReservation } from '@/services/reservations/queries';
import ModalDialog from '@/components/ui/modal/ModalDialog.vue';
import NewStatementFields from './NewStatementFields.vue';
import EligibleStatementsFields from './EligibleStatementsFields.vue';
import DeferralSuccess from './DeferralSuccess.vue';

defineOptions({ name: 'DeferReservationModal' });

const emit = defineEmits(['defer:success']);

// ─────────────────────────────────────────────────────────────
// Dependencies
// ─────────────────────────────────────────────────────────────
const statement = inject('statement');
const reservationDrawerStore = useReservationDrawerStore();

// ─────────────────────────────────────────────────────────────
// Template Refs & Local State
// ─────────────────────────────────────────────────────────────
const modalTarget = ref(null);
const eligibleStatementsFieldsRef = ref(null);
const newStatementFieldsRef = ref(null);
const shouldCreateNewStatement = ref(false);
const tempStatement = ref(null);

// ─────────────────────────────────────────────────────────────
// Computed Properties
// ─────────────────────────────────────────────────────────────
const reservationId = computed(() => (modalTarget.value?.isModalOpen ? reservationDrawerStore.reservationId : null));

// ─────────────────────────────────────────────────────────────
// Queries & Mutations
// ─────────────────────────────────────────────────────────────
const { data: eligibleStatements, isPending } = useGetEligibleStatements(reservationId, {
  per_page: 25,
});
const { mutate: createNewStatement, isPending: isCreatingNewStatement } = useCreateStatement();
const {
  mutate: deferReservationMutation,
  isPending: isDeferringReservation,
  isSuccess: isReservationDeferred,
} = useDeferReservation();

const isLoading = computed(() => isCreatingNewStatement.value || isDeferringReservation.value);

// ─────────────────────────────────────────────────────────────
// Modal Controls
// ─────────────────────────────────────────────────────────────
const showModal = () => modalTarget.value?.showModal();

const resetModal = () => {
  shouldCreateNewStatement.value = false;
  newStatementFieldsRef.value = null;
  if (isReservationDeferred.value) {
    emit('defer:success');
  }
};

// ─────────────────────────────────────────────────────────────
// Deferral Actions
// ─────────────────────────────────────────────────────────────
const deferReservation = (payload) => {
  deferReservationMutation(payload);
};

const deferWithNewStatement = (payload) => {
  createNewStatement(payload, {
    onSuccess: (res) => {
      tempStatement.value = res.data;
      deferReservation({
        id: reservationId.value,
        source_statement_id: statement.value.id,
        target_statement_id: res.data.id,
      });
    },
  });
};

const deferToExistingStatement = (selectedStatement) => {
  tempStatement.value = selectedStatement;
  deferReservation({
    id: reservationId.value,
    source_statement_id: statement.value.id,
    target_statement_id: selectedStatement.id,
  });
};

const handleDefer = () => {
  if (shouldCreateNewStatement.value) {
    newStatementFieldsRef.value?.triggerSubmit();
    return;
  }

  const selectedStatement = eligibleStatementsFieldsRef.value?.selectedStatement;
  if (!selectedStatement) {
    eligibleStatementsFieldsRef.value?.triggerValidationError();
    return;
  }

  deferToExistingStatement(selectedStatement);
};

// ─────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────
defineExpose({ showModal });
</script>
